import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Col from 'react-bootstrap/Col'
import { Button, Form as BsForm } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import Link from 'next/link'

const consentText = 'By submitting this form and signing up for texts, you consent to receive marketing text messages (e.g. promos, cart reminders) from company name at the number provided, including messages sent by autodialer. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available).'

const schema = z.object({
  businessType: z.enum(['retailer', 'liquidator'], { required_error: 'Please select one' }),
  businessName: z.string().min(2, 'Business name required'),
  name: z.string().min(2, 'Your name is required'),
  email: z.string().email('Invalid email'),
  phone: z.string().min(10, 'Phone required'),
  allow_marketing_sms: z.boolean().optional(),
  allow_marketing_emails: z.boolean().optional(),
})

export default function RetailersLandingForm ({ onSubmit, disabled }) {
  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      businessType: undefined,
      businessName: '',
      name: '',
      email: '',
      phone: '',
      allow_marketing_sms: false,
      allow_marketing_emails: false,
    }
  })

  const onFormSubmit = (data) => {
    onSubmit(data)
  }

  return (
      <BsForm onSubmit={handleSubmit(onFormSubmit)}>
        <div className={'d-flex flex-column gap-4'}>
          {/* Business Type */}
          <div>
            <label className="form-label">Do you represent a furniture retailer or liquidator?</label>
            <Controller
              control={control}
              name="businessType"
              render={({ field }) => (
                <div className="d-flex gap-4">
                  <BsForm.Check
                    type="radio"
                    label="Retailer"
                    id="retailer-radio"
                    value="retailer"
                    checked={field.value === 'retailer'}
                    onChange={() => field.onChange('retailer')}
                    isInvalid={!!formState.errors.businessType}
                    disabled={disabled}
                  />
                  <BsForm.Check
                    type="radio"
                    label="Liquidator"
                    id="liquidator-radio"
                    value="liquidator"
                    checked={field.value === 'liquidator'}
                    onChange={() => field.onChange('liquidator')}
                    isInvalid={!!formState.errors.businessType}
                    disabled={disabled}
                  />
                </div>
              )}
            />
            {formState.errors.businessType && (
              <div className="text-danger small mt-1">{formState.errors.businessType.message}</div>
            )}
          </div>
          {/* Business Name */}
          <div>
            <Controller
              control={control}
              name="businessName"
              render={({ field }) => (
                <BsForm.Group>
                  <BsForm.Label>Business Name</BsForm.Label>
                  <BsForm.Control
                    {...field}
                    placeholder="Business Name"
                    isInvalid={!!formState.errors.businessName}
                  />
                  <BsForm.Control.Feedback type="invalid">
                    {formState.errors.businessName?.message}
                  </BsForm.Control.Feedback>
                </BsForm.Group>
              )}
            />
          </div>
          {/* Your Name */}
          <div>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <BsForm.Group>
                  <BsForm.Label>Your Name</BsForm.Label>
                  <BsForm.Control
                    {...field}
                    placeholder="Your Name"
                    isInvalid={!!formState.errors.name}
                    disabled={disabled}
                  />
                  <BsForm.Control.Feedback type="invalid">
                    {formState.errors.name?.message}
                  </BsForm.Control.Feedback>
                </BsForm.Group>
              )}
            />
          </div>

          <Row>
            <Col xs={12} md={6}>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <BsForm.Group>
                    <BsForm.Label>Email</BsForm.Label>
                    <BsForm.Control
                      type="email"
                      {...field}
                      placeholder="Email"
                      isInvalid={!!formState.errors.email}
                      disabled={disabled}
                    />
                    <BsForm.Control.Feedback type="invalid">
                      {formState.errors.email?.message}
                    </BsForm.Control.Feedback>
                  </BsForm.Group>
                )}
              />
            </Col>
            <Col xs={12} md={6}>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <BsForm.Group>
                    <BsForm.Label>Phone Number</BsForm.Label>
                    <BsForm.Control
                      {...field}
                      placeholder="Phone Number"
                      isInvalid={!!formState.errors.phone}
                      disabled={disabled}
                    />
                    <BsForm.Control.Feedback type="invalid">
                      {formState.errors.phone?.message}
                    </BsForm.Control.Feedback>
                  </BsForm.Group>
                )}
              />
            </Col>
          </Row>

          <div className={'d-flex flex-column gap-2'}>
            <Controller
              control={control}
              name="allow_marketing_emails"
              render={({ field }) => (
                <BsForm.Check
                  type="checkbox"
                  label={'I would like to receive marketing emails from Ivy Flip'}
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  isInvalid={!!formState.errors.allow_marketing_emails}
                />
              )}
            />
            {formState.errors.allow_marketing_emails && (
              <div className="text-danger small mt-1">{formState.errors.allow_marketing_emails.message}</div>
            )}

            <Controller
              control={control}
              name="allow_marketing_sms"
              render={({ field }) => (
                <BsForm.Check
                  type="checkbox"
                  label={'I would like to receive marketing SMS from Ivy Flip'}
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  isInvalid={!!formState.errors.allow_marketing_sms}
                />
              )}
            />
            {formState.errors.allow_marketing_sms && (
              <div className="text-danger small mt-1">{formState.errors.allow_marketing_sms.message}</div>
            )}
          </div>

          <div>
            <Button
              type="submit"
              variant="success"
              size="lg"
              disabled={disabled}
            >
              Next
            </Button>
          </div>

          <div className={'text-muted'}>
            {consentText}
          </div>

          <div className={'d-flex flex-row gap-2'}>
            <Link
              href={'/legal/privacy-policy'}
              className={'text-muted'}
            >
              Privacy Policy
            </Link>

            <Link
              href={'/legal/terms-of-use'}
              className={'text-muted'}
            >
              Terms
            </Link>
          </div>
        </div>
      </BsForm>
  )
}
